import { useState } from "react";
import {
  CreditCard,
  Banknote,
  FileText,
  CircleDollarSign,
  MoreHorizontal,
  ChevronsUpDown,
} from "lucide-react";
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@b2b/ui-components";
import type { ProfileData } from "../types/profile";
import { cn } from "../../../lib/utils";

const PAYMENT_METHOD_OPTIONS: { value: string; label: string; icon: React.ReactNode }[] = [
  { value: "credit_card", label: "Credit Card", icon: <CreditCard className="h-4 w-4" /> },
  { value: "cash", label: "Cash", icon: <Banknote className="h-4 w-4" /> },
  { value: "cheque", label: "Cheque", icon: <FileText className="h-4 w-4" /> },
  { value: "demand_draft", label: "Demand Draft", icon: <FileText className="h-4 w-4" /> },
  { value: "paypal", label: "Paypal", icon: <CircleDollarSign className="h-4 w-4" /> },
  { value: "moneygram", label: "MoneyGram", icon: <CircleDollarSign className="h-4 w-4" /> },
  { value: "western_union", label: "Western Union", icon: <CircleDollarSign className="h-4 w-4" /> },
  { value: "others", label: "Others", icon: <MoreHorizontal className="h-4 w-4" /> },
];

const MAIN_MARKETS_OPTIONS = [
  { label: "North America", value: "north_america" },
  { label: "Europe", value: "europe" },
  { label: "Asia Pacific", value: "asia_pacific" },
  { label: "Middle East", value: "middle_east" },
  { label: "South America", value: "south_america" },
  { label: "Africa", value: "africa" },
  { label: "Domestic", value: "domestic" },
];

const CURRENCY_OPTIONS = [
  { label: "USD", value: "USD" },
  { label: "EUR", value: "EUR" },
  { label: "INR", value: "INR" },
  { label: "GBP", value: "GBP" },
  { label: "Other", value: "other" },
];

const PAYMENT_TERMS_OPTIONS = [
  { label: "Advance", value: "advance" },
  { label: "COD", value: "cod" },
  { label: "LC", value: "lc" },
];

const DURATION_OPTIONS = [
  { label: "Days", value: "days" },
  { label: "Weeks", value: "weeks" },
  { label: "Months", value: "months" },
];

const DELIVERY_TERMS_OPTIONS = [
  { label: "FOB", value: "fob" },
  { label: "CIF", value: "cif" },
  { label: "EXW", value: "exw" },
  { label: "DDP", value: "ddp" },
  { label: "CFR", value: "cfr" },
];

export type TradeSubSection = "market-logistics" | "shipping-payment" | "trade-additional";

interface TradeInformationSectionProps {
  subSection: TradeSubSection;
  formData: ProfileData;
  isEditing: boolean;
  onFieldChange: (fieldKey: string, value: string | File | boolean | string[]) => void;
  errors?: Record<string, string>;
}

function getValue(f: { value?: unknown } | undefined): string {
  if (f?.value === undefined || f?.value === null) return "";
  return typeof f.value === "string" ? f.value : String(f.value);
}

function getArrayValue(f: { value?: unknown } | undefined): string[] {
  if (!f?.value) return [];
  if (Array.isArray(f.value)) {
    return f.value.map((v) => (typeof v === "string" ? v : String(v)));
  }
  if (typeof f.value === "string" && f.value.trim()) {
    return [f.value.trim()];
  }
  return [];
}

type SelectOption = { label: string; value: string };

interface MultiSelectDropdownProps {
  label: string;
  placeholder: string;
  options: SelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
}

function MultiSelectDropdown({
  label,
  placeholder,
  options,
  selected,
  onChange,
  disabled = false,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);

  const toggleValue = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const selectedLabels = options
    .filter((o) => selected.includes(o.value))
    .map((o) => o.label);

  const displayText =
    selectedLabels.length > 0 ? selectedLabels.join(", ") : placeholder;

  if (disabled) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-medium text-gray-700">{label}</Label>
        <div className="w-full max-w-md min-h-10 px-3 py-2 rounded border border-gray-300 bg-gray-50 text-sm text-gray-700 flex items-center">
          {selectedLabels.length > 0 ? displayText : "Not set"}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full max-w-md h-10 justify-between border-gray-300 rounded bg-white font-normal text-gray-700 hover:bg-gray-50"
          >
            <span className="truncate">{displayText}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] min-w-[200px] max-w-md bg-white p-2 border border-gray-300 shadow-md"
          align="start"
        >
          <div className="space-y-1">
            {options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 rounded-md px-2 py-2 cursor-pointer hover:bg-gray-50"
              >
                <Checkbox
                  checked={selected.includes(opt.value)}
                  onCheckedChange={() => toggleValue(opt.value)}
                />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

export function TradeInformationSection({
  subSection,
  formData,
  isEditing,
  onFieldChange,
}: TradeInformationSectionProps) {
  const ci = formData.companyInfo || {};
  const mainMarkets = getArrayValue(ci.mainMarkets);
  const paymentMethods = getArrayValue(ci.paymentMethods);
  const acceptedPaymentCurrencies = getArrayValue(ci.acceptedPaymentCurrency);
  const paymentTermsSelected = getArrayValue(ci.paymentTerms);

  const toggleArray = (fieldKey: string, current: string[], value: string) => {
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    onFieldChange(fieldKey, next);
  };

  if (subSection === "market-logistics") {
    return (
      <div className="space-y-4">
        <MultiSelectDropdown
          label="Main Markets"
          placeholder="Select Main Markets"
          options={MAIN_MARKETS_OPTIONS}
          selected={mainMarkets}
          onChange={(values) => onFieldChange("mainMarkets", values)}
          disabled={!isEditing}
        />
      </div>
    );
  }

  if (subSection === "shipping-payment") {
    return (
      <div className="space-y-6">
        <MultiSelectDropdown
          label="Accepted Payment Currency"
          placeholder="Select Accepted Currency"
          options={CURRENCY_OPTIONS}
          selected={acceptedPaymentCurrencies}
          onChange={(values) => onFieldChange("acceptedPaymentCurrency", values)}
          disabled={!isEditing}
        />

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">Payment Methods</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {PAYMENT_METHOD_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={cn(
                  "flex items-center gap-2 rounded-lg border p-3 cursor-pointer transition-colors",
                  paymentMethods.includes(opt.value) ? "border-orange-500 bg-orange-50/50" : "border-gray-300 hover:bg-gray-50",
                )}
              >
                <Checkbox
                  checked={paymentMethods.includes(opt.value)}
                  onCheckedChange={() => toggleArray("paymentMethods", paymentMethods, opt.value)}
                  disabled={!isEditing}
                />
                <span className="text-muted-foreground">{opt.icon}</span>
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        <MultiSelectDropdown
          label="Payment Terms"
          placeholder="Select Payment Terms"
          options={PAYMENT_TERMS_OPTIONS}
          selected={paymentTermsSelected}
          onChange={(values) => onFieldChange("paymentTerms", values)}
          disabled={!isEditing}
        />
      </div>
    );
  }

  if (subSection === "trade-additional") {
    return (
      <div className="space-y-6">
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">Nearest Port</Label>
          <Input
            placeholder="Enter Nearest Port"
            value={getValue(ci.nearestPort)}
            onChange={(e) => onFieldChange("nearestPort", e.target.value)}
            disabled={!isEditing}
            className="max-w-md h-10 border-gray-300 rounded"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">Average Lead Time</Label>
          <div className="flex flex-wrap gap-2 items-center max-w-md">
            <Input
              placeholder="Enter Value"
              value={getValue(ci.averageLoadTimeValue)}
              onChange={(e) => onFieldChange("averageLoadTimeValue", e.target.value)}
              disabled={!isEditing}
              className="h-10 flex-1 min-w-[120px] border-gray-300 rounded"
            />
            <Select
              value={getValue(ci.averageLoadTimeUnit)}
              onValueChange={(v) => onFieldChange("averageLoadTimeUnit", v)}
              disabled={!isEditing}
            >
              <SelectTrigger className="h-10 w-[140px] border-gray-300 rounded bg-white">
                <SelectValue placeholder="Select Duration" />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-300 shadow-md z-50">
                {DURATION_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-gray-700">Accepted Delivery Terms</Label>
          <Select
            value={getValue(ci.acceptedDeliveryTerms)}
            onValueChange={(v) => onFieldChange("acceptedDeliveryTerms", v)}
            disabled={!isEditing}
          >
            <SelectTrigger className="w-full max-w-md h-10 border-gray-300 rounded bg-white">
              <SelectValue placeholder="Select Accepted Delivery Terms" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-300 shadow-md z-50">
              {DELIVERY_TERMS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    );
  }

  return null;
}
